import { ShieldAlert, CheckCircle, AlertTriangle, FileText, Users, ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";

const CrisisManagementService = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Business Continuity & Incident Response Planning | 4FOX Security"
        description="Prepare for and respond to cyber security incidents with comprehensive business continuity and incident response planning. Minimize downtime and protect your reputation."
        canonical="https://4fox.uk/services/crisis-management"
        schema={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Business Continuity and Incident Response Planning",
          "provider": {
            "@type": "Organisation",
            "name": "4FOX Security"
          },
          "serviceType": "Crisis Management and Incident Response",
          "areaServed": "GB"
        }}
      />
      <Navbar />
      
      <main className="pt-24 pb-16 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Hero Section */}
            <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/10 mb-6">
              <ShieldAlert className="w-8 h-8 text-accent" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Business Continuity and Incident Response Planning
            </h1>
            <p className="text-xl text-muted-foreground">
              Prepare for and respond to security incidents with comprehensive business continuity and incident response planning
            </p>
          </div>

          {/* Overview Section */}
          <div className="max-w-4xl mx-auto mb-16">
            <h2 className="text-3xl font-bold mb-6">Why Crisis Preparedness Matters</h2>
            <p className="text-lg text-muted-foreground mb-6">
              Cyber security incidents are not a matter of "if" but "when." The difference between a minor disruption and a catastrophic event often comes down to preparation. Our crisis management and incident response planning services ensure your organisation can respond effectively when incidents occur.
            </p>
            <p className="text-lg text-muted-foreground">
              We help you develop comprehensive plans, build capable response teams, and establish processes that minimise impact and enable rapid recovery.
            </p>
          </div>

          {/* Services Section */}
          <div className="max-w-4xl mx-auto mb-16">
            <h2 className="text-3xl font-bold mb-8">Our Crisis Management Services</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <FileText className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Incident Response Planning</h3>
                  <p className="text-muted-foreground">
                    Develop detailed incident response plans with clear procedures, roles, and communication protocols for various scenarios.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <CheckCircle className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Business Continuity Planning</h3>
                  <p className="text-muted-foreground">
                    Ensure critical business functions can continue during and after cyber incidents with robust continuity plans.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Crisis Communication</h3>
                  <p className="text-muted-foreground">
                    Establish clear communication strategies for internal teams, stakeholders, customers, and regulatory bodies.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <Users className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Team Training & Exercises</h3>
                  <p className="text-muted-foreground">
                    Prepare your team through tabletop exercises, simulations, and training to ensure readiness when incidents occur.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Key Components Section */}
          <div className="max-w-4xl mx-auto mb-16">
            <h2 className="text-3xl font-bold mb-8">Key Components of Our Planning</h2>
            <div className="space-y-4">
              <div className="p-6 border rounded-lg bg-card">
                <h3 className="text-xl font-semibold mb-2">Incident Classification & Escalation</h3>
                <p className="text-muted-foreground">
                  Clear criteria for identifying and escalating incidents based on severity and impact, ensuring appropriate response levels.
                </p>
              </div>
              <div className="p-6 border rounded-lg bg-card">
                <h3 className="text-xl font-semibold mb-2">Response Team Structure</h3>
                <p className="text-muted-foreground">
                  Define roles, responsibilities, and decision-making authority for incident response team members and leadership.
                </p>
              </div>
              <div className="p-6 border rounded-lg bg-card">
                <h3 className="text-xl font-semibold mb-2">Recovery Procedures</h3>
                <p className="text-muted-foreground">
                  Step-by-step recovery processes to restore systems, data, and operations while preserving evidence and learning from incidents.
                </p>
              </div>
              <div className="p-6 border rounded-lg bg-card">
                <h3 className="text-xl font-semibold mb-2">Post-Incident Review</h3>
                <p className="text-muted-foreground">
                  Structured processes for analysing incidents, identifying lessons learned, and continuously improving response capabilities.
                </p>
              </div>
            </div>
          </div>

          {/* Benefits Section */}
          <div className="max-w-4xl mx-auto mb-16">
            <h2 className="text-3xl font-bold mb-6">Benefits of Comprehensive Crisis Planning</h2>
            <ul className="space-y-3 text-lg text-muted-foreground">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-0.5" />
                <span>Minimise downtime and financial impact during security incidents</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-0.5" />
                <span>Protect reputation through effective crisis communication</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-0.5" />
                <span>Meet regulatory requirements for incident response and business continuity</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-0.5" />
                <span>Build organisational resilience and confidence in handling crises</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-0.5" />
                <span>Continuously improve security posture through lessons learned</span>
              </li>
            </ul>
          </div>

          {/* CTA Section */}
          <div className="text-center">
            <div className="p-8 bg-secondary/30 rounded-lg">
              <h2 className="text-3xl font-bold mb-4">Ready to Build Your Crisis Response Capability?</h2>
              <p className="text-lg text-muted-foreground mb-6">
                Let's discuss how we can help you prepare for and respond to cyber security incidents effectively.
              </p>
              <a 
                href="https://outlook.office365.com/owa/calendar/HazelMcPherson4FOXSecurity@4fox.uk/bookings/" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button size="lg" className="bg-accent hover:bg-accent/90">
                  Book a Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </div>
          </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CrisisManagementService;
